"use client";

import React from "react";
import { ReactRenderer, type Editor as ReactEditor } from "@tiptap/react";
import type { MentionItem, MentionSuggestionConfig } from "editor";
import MentionSuggestionList from "./mention-suggestion-list";
import type { MentionSuggestionListHandle } from "./mention-suggestion-list";

/**
 * Walk up from the editor's DOM node to find the nearest positioned
 * ancestor (the `.simple-editor-wrapper.relative` div in practice), so the
 * suggestion popup can be mounted inside it instead of `document.body`.
 *
 * Radix Dialog sets `document.body.style.pointerEvents = "none"` while
 * modal and only re-enables it on its own portalled content — an element
 * appended directly to `document.body` (as this used to do) silently
 * inherits that `pointer-events: none` and becomes unclickable even though
 * it's still visible. Same class of bug as the one documented in
 * `block-insert-menu.tsx` / `publish-menu.tsx`. Falls back to
 * `document.body` when no positioned ancestor exists (e.g. an editor that
 * isn't inside a Dialog), preserving the previous behavior there.
 */
function findPositionedAncestor(el: HTMLElement): HTMLElement {
  let node = el.parentElement;
  while (node && node !== document.body) {
    if (window.getComputedStyle(node).position !== "static") {
      return node;
    }
    node = node.parentElement;
  }
  return document.body;
}

/**
 * Creates a MentionSuggestionConfig to pass to useRichEditor's
 * mentionSuggestion option.
 *
 * `fetchMembers` is the Studio-specific data resolver — typically wraps
 * trpc.members.searchForMention.fetch(). Keeping this injected means
 * the factory is testable and doesn't import tRPC directly.
 *
 * Usage:
 *   const mentionSuggestion = useMemo(
 *     () => createMentionSuggestion(async (q) => {
 *       const r = await utils.members.searchForMention.fetch({ query: q });
 *       return r.items;
 *     }),
 *     [utils.members.searchForMention],
 *   );
 */
export function createMentionSuggestion(
  fetchMembers: (query: string) => Promise<MentionItem[]>,
): MentionSuggestionConfig {
  return {
    items: fetchMembers,

    render: () => {
      let renderer: ReactRenderer<MentionSuggestionListHandle> | null = null;
      let container: HTMLDivElement | null = null;
      let anchor: HTMLElement = document.body;

      const positionContainer = (
        clientRect: (() => DOMRect | null) | null | undefined,
      ): void => {
        if (!clientRect || !container) return;
        const rect = clientRect();
        if (!rect) return;
        const anchorRect = anchor.getBoundingClientRect();
        container.style.top = `${rect.bottom - anchorRect.top + anchor.scrollTop + 4}px`;
        container.style.left = `${rect.left - anchorRect.left + anchor.scrollLeft}px`;
      };

      return {
        onStart(props) {
          anchor = findPositionedAncestor(props.editor.view.dom);

          container = document.createElement("div");
          container.style.position = "absolute";
          container.style.zIndex = "9999";
          anchor.appendChild(container);

          renderer = new ReactRenderer(MentionSuggestionList, {
            props: {
              items: props.items as MentionItem[],
              command: (item: MentionItem) =>
                props.command({ id: item.id, label: item.label }),
            },
            editor: props.editor as unknown as ReactEditor,
          });

          if (renderer.element) {
            container.appendChild(renderer.element);
          }

          positionContainer(props.clientRect);
        },

        onUpdate(props) {
          renderer?.updateProps({
            items: props.items as MentionItem[],
            command: (item: MentionItem) =>
              props.command({ id: item.id, label: item.label }),
          });

          positionContainer(props.clientRect);
        },

        onKeyDown(props) {
          if (props.event.key === "Escape") {
            container?.remove();
            return true;
          }
          return renderer?.ref?.onKeyDown(props.event) ?? false;
        },

        onExit() {
          renderer?.destroy();
          container?.remove();
          renderer = null;
          container = null;
        },
      };
    },
  };
}
